import { Fragment, useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { LogOut, Menu, Check, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Toaster } from '@/components/ui/sonner'
import { useSession } from '@/lib/mobility-auth'
import { mobilityApi } from '@/lib/mobility'
import { entityStyle } from '@/lib/entity-colors'
import { cn } from '@/lib/utils'
import {
  isCollapsibleSection,
  portalForPath,
  portalsForRoles,
  type NavCountKey,
  type NavItem,
  type Portal,
} from './nav-config'

/**
 * The app shell (all four portals share it), rebuilt to the Axis Group Mobility
 * design language:
 *   • a 60px white header — gradient logo tile + Newsreader wordmark + the
 *     "Viewing as" portal pill (a dropdown switcher when the principal can
 *     reach >1 portal), entity name, sign out;
 *   • a 236px white sidebar per portal — uppercase group label, icon nav items
 *     with the active burgundy tint + inset accent, count pills (employee:
 *     own applications), and a footer helper line;
 *   • the main column (max-width 1120px) with the design's fade-in entry.
 * On mobile the sidebar collapses into the existing Sheet drawer. Renders the
 * active route via <Outlet/>. The <Toaster/> host is mounted here once.
 *
 * The active portal is derived from the current path (portalForPath); switching
 * portals navigates to the target portal's home — the role guards on each
 * subtree do the real gating.
 */
export function AppShell() {
  const { session, roles, logout } = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  const activePortal = portalForPath(location.pathname)
  const available = portalsForRoles(roles)
  const nav = activePortal.nav
  const counts = useNavCounts(activePortal.id === 'employee', location.pathname)

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const initials = (session?.employee_code ?? '??').slice(0, 2).toUpperCase()
  const avatarBg = entityStyle(session?.entity_name).color

  const portalPill = (
    <>
      <span
        className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
        style={{ background: avatarBg }}
        aria-hidden="true"
      >
        {initials}
      </span>
      <span className="flex min-w-0 flex-col items-start leading-[1.1]">
        <span className="max-w-[9rem] truncate text-[12.5px] font-semibold text-ink">
          {session?.employee_code ?? 'Signed in'}
        </span>
        <span className="text-[10px] font-medium text-ink-soft">{activePortal.label}</span>
      </span>
    </>
  )

  return (
    <div className="flex h-screen min-h-[640px] flex-col bg-background">
      <header className="z-20 flex h-[60px] shrink-0 items-center justify-between border-b border-border bg-surface px-4 sm:px-7">
        <div className="flex items-center gap-3">
          {/* Mobile nav trigger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
                <Menu className="size-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetHeader className="border-b border-border p-4">
                <SheetTitle className="text-left">
                  <BrandLockup subtitle={`${activePortal.label} portal`} />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col p-3" aria-label="Primary">
                <NavItems
                  portal={activePortal}
                  nav={nav}
                  counts={counts}
                  onNavigate={() => setMobileOpen(false)}
                />
              </nav>
            </SheetContent>
          </Sheet>

          <BrandLockup className="hidden sm:flex" />
          <BrandLockup hideWordmark className="sm:hidden" />
        </div>

        <div className="flex items-center gap-3 lg:gap-4">
          <span className="hidden text-xs text-ink-soft lg:inline">Viewing as</span>

          {available.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Switch portal"
                  className="flex items-center gap-2 rounded-[9px] border border-border bg-row py-[3px] pl-[3px] pr-2.5 transition-colors hover:border-[#D9C2CB]"
                >
                  {portalPill}
                  <ChevronsUpDown className="size-3.5 text-ink-soft" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Switch portal</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {available.map((p) => (
                  <DropdownMenuItem
                    key={p.id}
                    onSelect={() => navigate(p.home)}
                    className="justify-between"
                  >
                    {p.label}
                    {p.id === activePortal.id ? (
                      <Check className="size-4" aria-hidden="true" />
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="flex items-center gap-2 rounded-[9px] border border-border bg-row py-[3px] pl-[3px] pr-2.5">
              {portalPill}
            </span>
          )}

          {session?.entity_name ? (
            <span className="hidden text-sm text-ink-muted lg:inline" data-testid="entity-name">
              {session.entity_name}
            </span>
          ) : null}

          <Button variant="ghost" size="sm" onClick={onLogout} className="gap-1.5">
            <LogOut className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <nav
          className="hidden w-[236px] shrink-0 flex-col overflow-y-auto border-r border-border bg-surface px-3.5 py-[18px] md:flex"
          aria-label="Primary"
        >
          <NavItems portal={activePortal} nav={nav} counts={counts} />
          <div className="mt-auto border-t border-row px-3 pb-1 pt-3.5">
            <p className="text-[11px] leading-[1.5] text-ink-faint">
              Need help? Reach your
              <br />
              HR Partner or the TA team.
            </p>
          </div>
        </nav>

        <main className="min-w-0 flex-1 overflow-y-auto">
          <div
            key={location.pathname}
            className="omFade mx-auto max-w-[1120px] px-5 pb-16 pt-[34px] sm:px-10"
          >
            <Outlet />
          </div>
        </main>
      </div>
      <Toaster position="bottom-center" />
    </div>
  )
}

/** The header lock-up: gradient logo tile + Newsreader wordmark + subtitle. */
function BrandLockup({
  subtitle = 'Internal career marketplace',
  hideWordmark = false,
  className,
}: {
  subtitle?: string
  hideWordmark?: boolean
  className?: string
}) {
  return (
    <span className={cn('flex items-center gap-3', className)}>
      <span
        aria-hidden="true"
        className="flex size-[30px] shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-[#8E1537] to-[#B01E4C] text-[15px] font-bold tracking-[-0.5px] text-white"
      >
        A
      </span>
      {!hideWordmark && (
        <span className="flex min-w-0 flex-col leading-[1.15]">
          <span className="font-serif text-[17px] font-semibold text-ink">Axis Group Mobility</span>
          <span className="truncate text-[11px] tracking-[0.3px] text-ink-soft">{subtitle}</span>
        </span>
      )}
    </span>
  )
}

/** Uppercase sidebar group label. */
function NavGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    // Rhythm is deliberately ASYMMETRIC: a lot of space above, almost none
    // below. A heading belongs to the items it introduces, so it must sit
    // closer to them than to the group it just ended — the reverse (which this
    // had) makes every label read as free-floating noise. `first:pt-0` keeps
    // the top of the sidebar tight, since nothing precedes the first heading.
    // Weight is `medium`, not `semibold`: uppercase + letter-spacing already
    // carries the emphasis, and bolding on top of both is what made five of
    // these feel heavy.
    <div className="px-3 pb-[3px] pt-[15px] text-[10px] font-medium uppercase tracking-[0.7px] text-ink-faint first:pt-0">
      {children}
    </div>
  )
}

/**
 * The nav list, shared by the desktop sidebar and the mobile sheet so the two
 * can never drift.
 *
 * A portal whose items carry NO `section` renders exactly as it always did:
 * one `groupLabel` heading, then the items. A portal that DOES use sections
 * (Administration) prints each section label once, when it changes, instead of
 * the portal heading — thirteen equal peers gave an admin no way to see that
 * two of them are daily work and the rest are configuration.
 *
 * Printing on CHANGE (rather than grouping into sub-arrays) keeps `nav` a flat
 * list, so `portalsForRoles`, the tests and every other consumer are untouched.
 * The cost is that a section's items must be adjacent — stated on the type.
 */
function NavItems({
  portal,
  nav,
  counts,
  onNavigate,
}: {
  portal: Portal
  nav: NavItem[]
  counts: Partial<Record<NavCountKey, number>>
  onNavigate?: () => void
}) {
  const sectioned = nav.some((item) => item.section !== undefined)
  const { pathname } = useLocation()

  /**
   * Which collapsible sections are OPEN. A section starts closed — except the
   * one containing the current route, which opens on arrival: landing on
   * /admin/settings with "Advance" shut would show a sidebar in which nothing
   * is selected, and the item you are looking at appears not to exist.
   */
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const active = nav.find((item) => pathname.startsWith(item.to) && item.to !== '/admin')
    return isCollapsibleSection(active?.section) ? new Set([active!.section!]) : new Set()
  })
  // Navigating INTO a collapsed section (via a link elsewhere on the page)
  // opens it for the same reason.
  useEffect(() => {
    const active = nav.find((item) => pathname.startsWith(item.to) && item.to !== '/admin')
    if (isCollapsibleSection(active?.section)) {
      setOpenSections((prev) => (prev.has(active!.section!) ? prev : new Set(prev).add(active!.section!)))
    }
  }, [pathname, nav])

  const toggleSection = (section: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })

  let previous: string | undefined
  return (
    <>
      {sectioned ? null : <NavGroupLabel>{portal.groupLabel}</NavGroupLabel>}
      {nav.map((item) => {
        const openSection = item.section !== undefined && item.section !== previous
        previous = item.section
        const collapsible = isCollapsibleSection(item.section)
        // A collapsed section renders its heading (so it can be opened) and
        // none of its items.
        const hidden = collapsible && !openSections.has(item.section!)
        return (
          <Fragment key={item.to}>
            {openSection ? (
              collapsible ? (
                <NavSectionToggle
                  label={item.section!}
                  open={openSections.has(item.section!)}
                  onToggle={() => toggleSection(item.section!)}
                />
              ) : (
                <NavGroupLabel>{item.section}</NavGroupLabel>
              )
            ) : null}
            {hidden ? null : (
              <SideNavLink
                item={item}
                count={item.countKey ? counts[item.countKey] : undefined}
                onNavigate={onNavigate}
              />
            )}
          </Fragment>
        )
      })}
    </>
  )
}

/**
 * A section heading that is also a disclosure control.
 *
 * Styled as the plain {@link NavGroupLabel} plus a chevron rather than as a nav
 * row: it is still a heading, and making it look like an item would imply it
 * navigates somewhere. The whole strip is the hit area — a 10px chevron is not
 * a target anyone should have to aim at.
 */
function NavSectionToggle({
  label,
  open,
  onToggle,
}: {
  label: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`nav-section-${label.toLowerCase()}`}
      className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-3 pb-[3px] pt-[15px] text-left text-[10px] font-medium uppercase tracking-[0.7px] text-ink-faint transition-colors first:pt-0 hover:text-ink"
    >
      {label}
      <ChevronRight
        className={cn('size-3 shrink-0 transition-transform', open && 'rotate-90')}
        aria-hidden="true"
      />
    </button>
  )
}

/** A sidebar nav item: icon + label (+ optional count pill), design active state. */
function SideNavLink({
  item,
  count,
  onNavigate,
}: {
  item: NavItem
  count?: number
  onNavigate?: () => void
}) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          // py trimmed 9 -> 6.5px. The section headings now do the separating,
          // so each row no longer has to buy its own breathing room — 13 rows
          // paying for it twice is what pushed the sidebar past the viewport.
          'flex w-full items-center gap-[11px] rounded-lg px-3 py-[6.5px] text-left text-[13.5px] transition-colors',
          isActive
            ? 'bg-brand-subtle font-semibold text-brand shadow-[inset_3px_0_0_var(--primary)]'
            : 'font-medium text-[#4B4750] hover:bg-row/70 hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            // 18px against 13.5px text made the icon the loudest thing in the
            // row — it outweighed the label it was only meant to mark. 15px
            // sits just above the cap height, so the label leads and the icon
            // supports.
            className={cn('size-[15px] shrink-0', isActive ? 'text-brand' : 'text-[#9A94A0]')}
            aria-hidden="true"
          >
            {/*
              size-full on the ICON, not just the wrapper. The nav mixes two
              families: the design set renders width/height="100%" and obeyed
              the wrapper, while lucide icons default to 24x24 and ignored it —
              so exactly the seven lucide items rendered oversized while the
              rest looked right. Sizing the svg itself makes both obey, and any
              future icon from either family inherits the fix.
            */}
            <Icon className="size-full" />
          </span>
          <span className="flex-1">{item.label}</span>
          {/* Design fidelity: the prototype hides empty counts (`count || ''`). */}
          {count !== undefined && count > 0 ? (
            <span
              className={cn(
                'rounded-full px-[7px] py-px text-[11px] font-semibold',
                isActive ? 'bg-[#F2DDE4] text-brand' : 'bg-[#F1EEF2] text-ink-soft',
              )}
              {...(item.countLabel ? { title: `${count} ${item.countLabel}` } : {})}
            >
              {count}
              {item.countLabel ? <span className="sr-only"> {item.countLabel}</span> : null}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

/**
 * Cheap sidebar count pills for the employee portal: the principal's own
 * application total (list meta). Failures just hide the pills — never an error
 * surface in the chrome. Browse-roles no longer carries a pill: that number
 * lives on Discover, where the pagination total actually describes the board.
 *
 * RE-RESOLVED ON NAVIGATION (`pathname` is a dep, not just `enabled`). The
 * shell never unmounts while the principal moves around the portal, so a
 * mount-only fetch froze these pills at their first value: applying for a role
 * left "My applications" reading one less than the page it points at, and only
 * a full browser reload agreed. The pills are a PROJECTION of server state, so
 * they must re-read when the user moves.
 *
 * Out-of-order responses cannot post a stale count: the cleanup sets the
 * previous run's `cancelled` before the next effect runs, so only the latest
 * navigation's fetch is allowed to call `setCounts`.
 */
function useNavCounts(
  enabled: boolean,
  pathname: string,
): Partial<Record<NavCountKey, number>> {
  const [counts, setCounts] = useState<Partial<Record<NavCountKey, number>>>({})

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    void (async () => {
      try {
        // mine=true: a dual-role principal (staff + EMPLOYEE) otherwise gets
        // the STAFF scope here and the pill counts everyone's applications.
        const apps = await mobilityApi.applications.list({ page: 1, limit: 1, mine: true })
        if (cancelled) return
        setCounts({
          myApplications: apps.meta.pagination?.totalItems ?? apps.data.length,
        })
      } catch {
        // Leave the pills hidden — the pages themselves surface load errors.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [enabled, pathname])

  return counts
}
